import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { appointmentFormBodySchema, appointmentFormCustomFieldSchema, appointmentFormDetailSchema, calendarSettingsRowSchema, crmPrivacyDocumentResponseSchema, type AppointmentFormBody, type AppointmentFormLogoUpload, type UserSitePhotoUpload } from '@easyflow/shared';
import {
  Plus, Search, Code2, Trash2, Eye, Loader2, Save, ArrowLeft, Calendar,
  Palette, Briefcase, Users, Image as ImageIcon, Settings, Shield, Bell,
  ListChecks, Sparkles, Upload, X, ExternalLink,
 KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import FormCoherencePanel from '@/components/calendar/FormCoherencePanel';
import ServiceCardList from '@/components/calendar/ServiceCardList';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import EmbedCodeDialog from '@/components/embed/EmbedCodeDialog';
import PublicKeyDialog from '@/components/embed/PublicKeyDialog';
import AppointmentWidget from '@/components/appointment-widget/AppointmentWidget';
import { mapCustomFields } from '@/components/appointment-widget/api';
import { getErrorMessage } from '../../lib/utils';
import type {
  AppointmentFormConfig,
  ServiceItem,
  ConsultantItem,
  WidgetStyle,
} from '@/components/appointment-widget/types';

// Task #2723 — opzione minima per il picker sede del form.
interface StoreOption {
  id: string;
  name: string;
  city?: string | null;
}

const FONT_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Predefinito (di sistema)', value: '' },
  { label: 'Inter', value: "'Inter', system-ui, sans-serif" },
  { label: 'Roboto', value: "'Roboto', system-ui, sans-serif" },
  { label: 'Poppins', value: "'Poppins', system-ui, sans-serif" },
  { label: 'Open Sans', value: "'Open Sans', system-ui, sans-serif" },
  { label: 'Lato', value: "'Lato', system-ui, sans-serif" },
  { label: 'Montserrat', value: "'Montserrat', system-ui, sans-serif" },
  { label: 'Playfair Display (serif)', value: "'Playfair Display', Georgia, serif" },
  { label: 'Merriweather (serif)', value: "'Merriweather', Georgia, serif" },
  { label: 'Custom CSS…', value: '__custom__' },
];

interface ListItem {
  id: string;
  slug: string;
  name: string;
  is_active: boolean;
  style: WidgetStyle;
  submissions_count?: number;
}

interface ApiServiceType {
  id: string;
  name: string;
  description?: string;
  duration?: number;
  slot_duration?: number;
  color?: string;
  linked_services?: Array<{ id: string; name: string; description?: string | null; price?: number | null }>;
  linked_services_count?: number;
}

interface ApiUser {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  site_photo_url?: string;
  bio?: string;
  skills?: Array<{ name: string; level?: number }>;
}

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])?$/;

const customFieldSchema = appointmentFormCustomFieldSchema.extend({
  label: z.string().min(1, 'Etichetta richiesta'),
});

const formSchema = z.object({
  id: z.string(),
  slug: z.string().min(3, 'Min 3 caratteri').max(64, 'Max 64 caratteri').regex(SLUG_REGEX, 'Solo a-z, 0-9 e trattini'),
  name: z.string().min(2, 'Nome richiesto'),
  // Task #2723 — sede di default applicata agli appuntamenti nati dal form.
  storeId: z.string().uuid('Sede richiesta'),
  // Task #2752 — quando true il cliente sceglie la sede nel form pubblico
  // (la sede di default resta il fallback se non sceglie).
  allowStoreChoice: z.boolean(),
  description: z.string().optional(),
  isActive: z.boolean(),
  style: z.enum(['calcom', 'doctolib']),
  branding: z.object({
    primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Colore esadecimale richiesto'),
    accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Colore esadecimale richiesto'),
    logoUrl: z.string().url().optional().or(z.literal('')),
    fontFamily: z.string().optional(),
    defaultTheme: z.enum(['light', 'dark']),
  }),
  serviceIds: z.array(z.string()),
  // Whitelist prodotti (servizi card) per-form. Vuoto = tutti i prodotti
  // collegati alle tipologie selezionate sono visibili nel widget pubblico.
  productIds: z.array(z.string()),
  consultantIds: z.array(z.string()),
  // Task #2786 + #2846 — modalità consulente unica (sostituisce allowConsultantChoice +
  // allowAnyConsultant). 'any' = il sistema assegna; 'skill_shifts' = il cliente
  // sceglie tra i consulenti idonei (competenza del servizio) con overlay turni;
  // 'manual' = roster whitelist esplicito. La vecchia modalità 'skill' (senza
  // turni) è stata fusa in 'skill_shifts'.
  consultantFilterMode: z.enum(['any', 'skill_shifts', 'manual']),
  // Task #2846 — flag "filtro competenze" valido SOLO in modalità 'manual': se
  // attivo, la whitelist viene ulteriormente ristretta ai consulenti che hanno
  // la competenza richiesta dal servizio/prodotto scelto. Inerte negli altri modi.
  consultantSkillFilter: z.boolean(),
  // Task #515 + #525: come gestire la selezione del "Servizio" (prodotto/pos_products)
  // 'required' è la nuova modalità che impone la scelta del prodotto al cliente.
  serviceSelectionMode: z.enum(['auto', 'always', 'required', 'never']),
  // Toggle "Mostra prezzo nel widget pubblico" (default true).
  showPrice: z.boolean(),
  rules: z.object({
    autoConfirm: z.boolean(),
    cancelWindowHours: z.number().int().min(0).max(720),
    rescheduleWindowHours: z.number().int().min(0).max(720),
  }),
  customFields: z.array(customFieldSchema),
  customFieldsSource: z.array(z.unknown()),
  privacy: z.object({
    privacyText: z.string(),
    marketingOptIn: z.boolean(),
    successMessage: z.string().min(1),
    redirectUrl: z.string().optional(),
  }),
  notifications: z.object({
    emailCustomer: z.boolean(),
    emailConsultant: z.boolean(),
    attachIcs: z.boolean(),
  }),
  antiAbuse: z.object({
    captchaProvider: z.enum(['none', 'hcaptcha', 'recaptcha']),
    captchaSiteKey: z.string().optional(),
    captchaSecretKey: z.string().optional(),
    // Read-only flags reflecting the persisted state of this form, used to
    // decide whether the Secret Key input is mandatory. We track *which*
    // provider the stored secret was configured for: if the admin switches
    // provider, the stored secret is meaningless and a new one must be typed.
    captchaSecretConfigured: z.boolean().optional(),
    captchaSecretConfiguredFor: z.enum(['none', 'hcaptcha', 'recaptcha']).optional(),
  }),
}).refine(
  (d) => d.antiAbuse.captchaProvider === 'none' || (d.antiAbuse.captchaSiteKey && d.antiAbuse.captchaSiteKey.length > 0),
  { message: 'Site key richiesta quando il captcha è attivo', path: ['antiAbuse', 'captchaSiteKey'] },
).refine(
  (d) => {
    if (d.antiAbuse.captchaProvider === 'none') return true;
    // Trim before considering the input "filled" so that whitespace-only
    // values don't bypass the requirement on the client (the server would
    // otherwise reduce them to null and silently keep the form invalid).
    const hasNew = !!(d.antiAbuse.captchaSecretKey && d.antiAbuse.captchaSecretKey.trim().length > 0);
    if (hasNew) return true;
    // The previously configured secret is only valid if the provider didn't
    // change since it was saved.
    return !!d.antiAbuse.captchaSecretConfigured
      && d.antiAbuse.captchaSecretConfiguredFor === d.antiAbuse.captchaProvider;
  },
  { message: 'Secret key richiesta quando il captcha è attivo (o quando si cambia provider)', path: ['antiAbuse', 'captchaSecretKey'] },
);

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
  id: '',
  slug: '',
  name: 'Nuovo form prenotazioni',
  storeId: '',
  allowStoreChoice: false,
  description: '',
  isActive: true,
  style: 'calcom',
  branding: {
    primaryColor: '#6366f1',
    accentColor: '#ec4899',
    logoUrl: '',
    fontFamily: '',
    defaultTheme: 'light',
  },
  serviceIds: [],
  productIds: [],
  consultantIds: [],
  consultantFilterMode: 'skill_shifts',
  consultantSkillFilter: false,
  serviceSelectionMode: 'auto',
  showPrice: false,
  rules: { autoConfirm: true, cancelWindowHours: 24, rescheduleWindowHours: 24 },
  customFields: [],
  customFieldsSource: [],
  privacy: {
    privacyText: 'Accetto l\'informativa sulla privacy *',
    marketingOptIn: false,
    successMessage: 'Prenotazione confermata!',
    redirectUrl: '',
  },
  notifications: { emailCustomer: true, emailConsultant: true, attachIcs: true },
  antiAbuse: {
    captchaProvider: 'none',
    captchaSiteKey: '',
    captchaSecretKey: '',
    captchaSecretConfigured: false,
    captchaSecretConfiguredFor: 'none',
  },
};

/**
 * Task #507 — snippet builder.
 *
 * The new canonical URL pattern is `/<tenantSlug>/booking/<formSlug>` with
 * the public booking API mounted at `/<tenantSlug>/booking/api`. We always
 * emit `data-tenant-slug` + a tenant-scoped `data-api-base` so a single
 * tenant's widgets can never accidentally talk to another tenant's API.
 */
function buildEmbedCode(slug: string, tenantSlug: string, publicKeyPrefix?: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const ts = tenantSlug || 'TUO_TENANT';
  const apiBase = `${origin}/${ts}/booking/api`;
  // We never inject the FULL pk_ value here — only a non-secret hint of the
  // prefix so users can verify they pasted the right one. The user pastes
  // their own pk_live_ from the "Chiave pubblica" dialog.
  const pkValue = 'INSERISCI_LA_TUA_PUBLIC_KEY';
  const hint = publicKeyPrefix
    ? `\n<!-- ✓ Esiste già una chiave pubblica per questo form (prefisso ${publicKeyPrefix}…). Incolla il valore completo che hai salvato. -->`
    : `\n<!-- ⚠ Genera prima una chiave pubblica con il pulsante "Chiave pubblica" e sostituisci INSERISCI_LA_TUA_PUBLIC_KEY. -->`;
  return `<!-- EDG VoIP — Appointment Widget -->${hint}
<div data-form-slug="${slug}"
     data-tenant-slug="${ts}"
     data-api-base="${apiBase}"></div>
<link rel="stylesheet" href="${origin}/widget/appointment-widget.css" />
<script src="${origin}/widget/appointment-widget.js" data-pk="${pkValue}" defer></script>`;
}

export function toFormValues(input: unknown): FormValues {
  const d = appointmentFormDetailSchema.parse(input);
  // The admin GET /forms/:id returns the raw row plus joined services/consultants arrays.
  const services = d.services;
  const consultants = d.consultants;
  const products = d.products;
  return {
    id: String(d.id || ''),
    slug: String(d.slug || ''),
    name: String(d.name || ''),
    storeId: d.store_id || '',
    allowStoreChoice: d.allow_store_choice ?? false,
    description: d.description || '',
    isActive: d.is_active ?? true,
    style: d.widget_style || 'calcom',
    branding: { ...DEFAULT_VALUES.branding, ...d.branding },
    serviceIds: services.map((s) => s.service_type_id),
    productIds: products.map((p) => p.product_id),
    consultantIds: consultants.map((c) => c.user_id),
    // Task #2786 — modalità unica dal backend; fallback ai booleani legacy
    // (rolling-deploy / record non ancora migrati): choice=false → 'any'.
    // Task #2846 — la vecchia modalità 'skill' (senza turni) è stata fusa in
    // 'skill_shifts'; i record legacy non ancora migrati la rimappano qui.
    consultantFilterMode:
      (d.consultant_filter_mode === 'any'
        || d.consultant_filter_mode === 'skill_shifts'
        || d.consultant_filter_mode === 'manual')
        ? d.consultant_filter_mode
        : (d.allow_consultant_choice === false ? 'any' : 'skill_shifts'),
    // Task #2846 — flag filtro competenze (valido solo in 'manual').
    consultantSkillFilter: d.consultant_skill_filter ?? false,
    serviceSelectionMode:
      (d.service_selection_mode === 'always'
        || d.service_selection_mode === 'never'
        || d.service_selection_mode === 'required')
        ? d.service_selection_mode
        : 'auto',
    showPrice: d.show_price === true,
    rules: {
      autoConfirm: d.auto_confirm ?? true,
      cancelWindowHours: d.cancel_deadline_hours ?? 24,
      rescheduleWindowHours: d.reschedule_deadline_hours ?? 24,
    },
    customFields: (d.custom_fields ?? []).flatMap(value => {
      const parsed = customFieldSchema.safeParse(value);
      return parsed.success ? [parsed.data] : [];
    }),
    customFieldsSource: d.custom_fields ?? [],
    privacy: {
      privacyText: d.privacy_text || DEFAULT_VALUES.privacy.privacyText,
      marketingOptIn: !!d.marketing_opt_in,
      successMessage: d.confirmation_message || DEFAULT_VALUES.privacy.successMessage,
      redirectUrl: d.redirect_url || '',
    },
    notifications: {
      emailCustomer: d.send_customer_email ?? true,
      emailConsultant: d.send_consultant_email ?? true,
      attachIcs: d.attach_ics ?? true,
    },
    antiAbuse: {
      captchaProvider:
        d.captcha_provider === 'hcaptcha'
          ? 'hcaptcha'
          : d.captcha_provider === 'recaptcha'
            ? 'recaptcha'
            : 'none',
      captchaSiteKey: d.captcha_site_key || '',
      captchaSecretKey: '',
      captchaSecretConfigured: !!d.captcha_secret_configured,
      captchaSecretConfiguredFor:
        d.captcha_provider === 'hcaptcha'
          ? 'hcaptcha'
          : d.captcha_provider === 'recaptcha'
            ? 'recaptcha'
            : 'none',
    },
  };
}

function mergeCustomFields(v: Pick<FormValues, 'customFields' | 'customFieldsSource'>): unknown[] {
  const remaining = [...v.customFields];
  const existing = v.customFieldsSource.flatMap<unknown>(value => {
    const parsed = customFieldSchema.safeParse(value);
    if (!parsed.success) return [value];
    const index = remaining.findIndex(field => field.id === parsed.data.id);
    if (index < 0) return []; // An editable field was deliberately removed.
    return remaining.splice(index, 1);
  });
  return [...existing, ...remaining];
}

export function toApiPayload(v: FormValues): AppointmentFormBody {
  return appointmentFormBodySchema.parse({
    name: v.name,
    slug: v.slug,
    store_id: v.storeId || null,
    allow_store_choice: v.allowStoreChoice,
    description: v.description || null,
    is_active: v.isActive,
    widget_style: v.style,
    branding: v.branding,
    service_mode: v.serviceIds.length > 0 ? 'whitelist' : 'all',
    // Task #2830 — la whitelist roster (consultant_ids/consultant_mode) è
    // utilizzabile SOLO in modalità 'manual'; negli altri modi il pool è
    // governato da skill/turni e la whitelist non deve filtrare nulla.
    // In 'manual' il mode è SEMPRE 'whitelist' (anche con lista vuota → 0
    // consulenti): mai fallback a 'all', che esporrebbe tutti i consulenti.
    consultant_mode: v.consultantFilterMode === 'manual' ? 'whitelist' : 'all',
    service_ids: v.serviceIds,
    product_ids: v.productIds,
    consultant_ids: v.consultantFilterMode === 'manual' ? v.consultantIds : [],
    // Task #2786 — solo la modalità unica; il backend deriva i booleani legacy.
    consultant_filter_mode: v.consultantFilterMode,
    // Task #2846 — flag filtro competenze: persistito sempre, ma significativo
    // SOLO in modalità 'manual' (il backend lo applica solo lì + con prodotto).
    consultant_skill_filter: v.consultantFilterMode === 'manual' ? v.consultantSkillFilter : false,
    // Task #515
    service_selection_mode: v.serviceSelectionMode,
    show_price: v.showPrice,
    auto_confirm: v.rules.autoConfirm,
    custom_fields: mergeCustomFields(v),
    privacy_text: v.privacy.privacyText || null,
    marketing_opt_in: !!v.privacy.marketingOptIn,
    confirmation_message: v.privacy.successMessage || null,
    redirect_url: v.privacy.redirectUrl || null,
    send_customer_email: v.notifications.emailCustomer,
    send_consultant_email: v.notifications.emailConsultant,
    attach_ics: v.notifications.attachIcs,
    captcha_provider: v.antiAbuse.captchaProvider === 'none' ? null : v.antiAbuse.captchaProvider,
    captcha_site_key: v.antiAbuse.captchaProvider === 'none' ? null : (v.antiAbuse.captchaSiteKey || null),
    // Secret-key payload rules:
    // - Disabling captcha → null (clear stored secret)
    // - User typed a new value → send it (will be encrypted server-side)
    // - Provider changed since the saved secret → null, to avoid keeping a
    //   stale cross-provider secret around (validation also blocks save in
    //   this case unless a new secret is typed)
    // - Otherwise omit the field so we don't overwrite the stored value
    ...(v.antiAbuse.captchaProvider === 'none'
      ? { captcha_secret_key: null }
      : v.antiAbuse.captchaSecretKey && v.antiAbuse.captchaSecretKey.trim().length > 0
        ? { captcha_secret_key: v.antiAbuse.captchaSecretKey.trim() }
        : v.antiAbuse.captchaSecretConfigured
          && v.antiAbuse.captchaSecretConfiguredFor !== v.antiAbuse.captchaProvider
          ? { captcha_secret_key: null }
          : {}),
    cancel_deadline_hours: v.rules.cancelWindowHours,
    reschedule_deadline_hours: v.rules.rescheduleWindowHours,
  });
}

// Larghezza nativa del widget pubblico nell'anteprima.
// Sotto questa soglia entra in azione lo zoom-out tramite CSS transform.
const PREVIEW_NATIVE_WIDTH = 720;

/**
 * Wrapper che renderizza il widget pubblico alla sua larghezza nativa
 * (PREVIEW_NATIVE_WIDTH) e lo rimpicciolisce con `transform: scale()` per
 * riempire la larghezza del contenitore. Mantiene il rapporto e ricalcola
 * l'altezza del wrapper esterno per evitare spazio vuoto.
 *
 * Niente scroll orizzontale: l'anteprima si adatta sempre al pannello.
 */
function ScaledPreview({ children }: { children: React.ReactNode }) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [innerHeight, setInnerHeight] = useState<number>(0);

  // Osserva la larghezza disponibile del container esterno e l'altezza reale
  // del widget interno, ricalcolando lo scale e l'altezza compensata.
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const recompute = () => {
      const availableWidth = outer.clientWidth;
      const next = availableWidth >= PREVIEW_NATIVE_WIDTH ? 1 : Math.max(0.4, availableWidth / PREVIEW_NATIVE_WIDTH);
      setScale(next);
      // L'altezza naturale del contenuto va moltiplicata per lo scale così il
      // box esterno occupa lo spazio "scalato" e non lascia un buco sotto.
      setInnerHeight(inner.scrollHeight * next);
    };

    recompute();

    const ro = new ResizeObserver(() => recompute());
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [children]);

  return (
    <div
      ref={outerRef}
      className="bg-white dark:bg-gray-900 rounded-lg sm:rounded-xl shadow-sm overflow-hidden w-full"
      style={{ height: innerHeight || undefined }}
    >
      <div
        ref={innerRef}
        style={{
          width: PREVIEW_NATIVE_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function AppointmentFormsTab() {
  const { toast } = useToast();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [items, setItems] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [editingValues, setEditingValues] = useState<FormValues | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedSlug, setEmbedSlug] = useState('');
  const [embedFormName, setEmbedFormName] = useState('');
  const [embedFormId, setEmbedFormId] = useState<string | undefined>(undefined);

  // Task #507 — dedicated dialog to manage the public key bound to a form
  const [pkDialogOpen, setPkDialogOpen] = useState(false);
  const [pkDialogFormId, setPkDialogFormId] = useState<string>('');
  const [pkDialogFormName, setPkDialogFormName] = useState<string>('');
  const [pkDialogFormSlug, setPkDialogFormSlug] = useState<string>('');

  const [availableServices, setAvailableServices] = useState<ServiceItem[]>([]);
  const [availableConsultants, setAvailableConsultants] = useState<ConsultantItem[]>([]);
  // Task #2723 — sedi selezionabili come default del form (picker store).
  const [availableStores, setAvailableStores] = useState<StoreOption[]>([]);

  const [formsWithKey, setFormsWithKey] = useState<Set<string>>(new Set());

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<unknown>('/calendar/forms');
      if (!res.success) throw new Error('Errore caricamento form');
      const forms = z.array(appointmentFormDetailSchema).parse(res.data);
      setItems(forms.map((item) => ({
        id: item.id, slug: item.slug, name: item.name,
        is_active: item.is_active ?? true, style: item.widget_style ?? 'calcom',
        submissions_count: typeof item.submissions_count === 'number' ? item.submissions_count : undefined,
      })));
    } catch {
      toast({ title: 'Impossibile caricare i form di prenotazione', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Task #507 — track which forms already have at least one public key so the
  // "Codice Embed" row action can be disabled (with tooltip) until a pk_ exists.
  const loadPublicKeys = useCallback(async () => {
    try {
      const res = await apiClient.get<Array<{ id: string; allowed_form_ids?: string[]; key_type?: string; allowedFormIds?: string[]; keyType?: string }>>(
        '/api-keys?keyType=public',
      );
      const list = res?.data ?? [];
      const ids = new Set<string>();
      for (const k of list) {
        const fids: string[] = k.allowed_form_ids || k.allowedFormIds || [];
        for (const id of fids) ids.add(id);
      }
      setFormsWithKey(ids);
    } catch {
      // best-effort; on failure all rows fall back to "no key" gating
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    // Task #2752: ogni fetch è indipendente — un errore (o una shape inattesa)
    // su una chiamata NON deve azzerare le altre. Prima un throw su `/stores`
    // (risposta paginata `{ items }` mappata come array) abortiva l'intero
    // catalogo, lasciando tipologie/consulenti/sedi vuoti nell'editor.
    try {
      const typesRes = await apiClient.get<ApiServiceType[]>('/calendar/types');
      if (typesRes.success) {
        setAvailableServices(
          (typesRes.data || []).map((t) => ({
            id: t.id,
            name: t.name,
            description: t.description,
            durationMinutes: t.duration || t.slot_duration || 30,
            color: t.color || '#6366f1',
            products: (t.linked_services || []).map((p) => ({
              id: p.id,
              name: p.name,
              description: p.description || undefined,
              price: typeof p.price === 'number' ? p.price : null,
            })),
          })),
        );
      }
    } catch {
      // ignore — il blocco tipologie non deve impattare gli altri
    }

    try {
      const usersRes = await apiClient.get<ApiUser[]>('/calendar/users');
      if (usersRes.success) {
        setAvailableConsultants(
          (usersRes.data || []).map((u) => ({
            id: u.id,
            firstName: u.first_name || u.name?.split(' ')?.[0] || '',
            lastName: u.last_name || u.name?.split(' ')?.slice(1).join(' ') || '',
            fullName: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
            email: u.email,
            sitePhotoUrl: u.site_photo_url,
            bio: u.bio,
            skills: u.skills || [],
          })),
        );
      }
    } catch {
      // ignore — il blocco consulenti non deve impattare gli altri
    }

    try {
      // `/stores` è paginato: la lista è in `data.items`, non in `data`.
      const storesRes = await apiClient.get<{ items: StoreOption[] }>('/stores');
      if (storesRes.success) {
        const list = storesRes.data?.items ?? [];
        setAvailableStores(list.map((s) => ({ id: s.id, name: s.name, city: s.city })));
      }
    } catch {
      // ignore — il blocco sedi non deve impattare gli altri
    }
  }, []);

  useEffect(() => {
    loadList();
    loadCatalog();
    loadPublicKeys();
  }, [loadList, loadCatalog, loadPublicKeys]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filterActive === 'active' && !it.is_active) return false;
      if (filterActive === 'inactive' && it.is_active) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return it.name.toLowerCase().includes(q) || it.slug.toLowerCase().includes(q);
    });
  }, [items, search, filterActive]);

  const openNew = () => {
    setEditingValues({ ...DEFAULT_VALUES });
    setEditingId(null);
  };

  const openEdit = async (id: string) => {
    try {
      const res = await apiClient.get<unknown>(`/calendar/forms/${id}`);
      if (res.success && res.data) {
        setEditingValues(toFormValues(res.data));
        setEditingId(id);
      }
    } catch {
      toast({ title: 'Errore caricamento form', variant: 'destructive' });
    }
  };

  const deleteForm = async (id: string) => {
    if (!confirm('Eliminare questo form? Questa azione non si può annullare.')) return;
    try {
      await apiClient.delete(`/calendar/forms/${id}`);
      toast({ title: 'Form eliminato' });
      loadList();
      if (editingId === id) {
        setEditingValues(null);
        setEditingId(null);
      }
    } catch {
      toast({ title: 'Errore eliminazione', variant: 'destructive' });
    }
  };

  const openEmbed = (slug: string, name: string, formId?: string) => {
    setEmbedSlug(slug);
    setEmbedFormName(name);
    setEmbedFormId(formId);
    setEmbedOpen(true);
  };

  if (editingValues) {
    const existingSlugs = items
      .filter((it) => it.id !== editingId)
      .map((it) => it.slug);
    return (
      <EditorView
        defaultValues={editingValues}
        editingId={editingId}
        existingSlugs={existingSlugs}
        onBack={() => { setEditingValues(null); setEditingId(null); }}
        onSaved={(savedId, slug, name) => {
          loadList();
          if (!editingId && savedId) setEditingId(savedId);
          toast({ title: editingId ? 'Form aggiornato' : 'Form creato' });
          setEmbedSlug(slug);
          setEmbedFormName(name);
        }}
        availableServices={availableServices}
        availableConsultants={availableConsultants}
        availableStores={availableStores}
        onOpenEmbed={(slug, name, formId) => openEmbed(slug, name, formId)}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-indigo-500 dark:text-indigo-400" />
            Form Appuntamenti
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Crea e personalizza form di prenotazione pubblici per i tuoi clienti</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4 mr-2" /> Nuovo form
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <Input
                placeholder="Cerca per nome o slug…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterActive} onValueChange={(v: 'all' | 'active' | 'inactive') => setFilterActive(v)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="active">Solo attivi</SelectItem>
                <SelectItem value="inactive">Solo disattivi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Nessun form configurato</p>
              <p className="text-sm mt-1">Crea il primo form per permettere ai tuoi clienti di prenotare online</p>
              <Button className="mt-4" onClick={openNew}>
                <Plus className="w-4 h-4 mr-2" /> Crea form
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((it) => (
                <div key={it.id} className="border rounded-lg p-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{it.name}</span>
                      {it.is_active ? (
                        <Badge variant="outline" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700 text-[10px]">Attivo</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-[10px]">Disattivo</Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] capitalize">{it.style}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">/{it.slug} · {it.submissions_count ?? 0} prenotazioni</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[40px]"
                    onClick={() => {
                      setPkDialogFormId(it.id);
                      setPkDialogFormName(it.name);
                      setPkDialogFormSlug(it.slug);
                      setPkDialogOpen(true);
                    }}
                    title="Gestisci chiave pubblica per questo form"
                  >
                    <KeyRound className="w-4 h-4 mr-1" /> Chiave pubblica
                  </Button>
                  {(() => {
                    const hasPk = formsWithKey.has(it.id);
                    return (
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-[40px]"
                        disabled={!hasPk}
                        title={hasPk
                          ? 'Mostra snippet embed per questo form'
                          : 'Genera prima una "Chiave pubblica" per abilitare lo snippet embed'}
                        onClick={() => openEmbed(it.slug, it.name, it.id)}
                      >
                        <Code2 className="w-4 h-4 mr-1" /> Codice Embed
                      </Button>
                    );
                  })()}
                  <Button variant="outline" size="sm" className="min-h-[40px]" onClick={() => openEdit(it.id)}>
                    Modifica
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteForm(it.id)}>
                    <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <EmbedCodeDialog
        open={embedOpen}
        onOpenChange={setEmbedOpen}
        title={`Codice Embed - ${embedFormName}`}
        description="Copia questo codice e incollalo nel tuo sito web per mostrare il form di prenotazione. Le prenotazioni verranno create automaticamente nel tuo calendario."
        embedCode={(pkPrefix?: string) => buildEmbedCode(embedSlug, tenantSlug || '', pkPrefix)}
        previewUrl={embedSlug && tenantSlug ? `/${tenantSlug}/booking/${embedSlug}` : undefined}
        formId={embedFormId}
        onOpenPublicKeyDialog={() => {
          setPkDialogFormId(embedFormId || '');
          setPkDialogFormName(embedFormName);
          setPkDialogFormSlug(embedSlug);
          setEmbedOpen(false);
          setPkDialogOpen(true);
        }}
      />

      <PublicKeyDialog
        open={pkDialogOpen}
        onOpenChange={(o) => {
          setPkDialogOpen(o);
          if (!o) loadPublicKeys();
        }}
        formId={pkDialogFormId}
        formName={pkDialogFormName}
        formSlug={pkDialogFormSlug}
      />
    </div>
  );
}

interface EditorProps {
  defaultValues: FormValues;
  editingId: string | null;
  existingSlugs: string[];
  onBack: () => void;
  onSaved: (id: string, slug: string, name: string) => void;
  availableServices: ServiceItem[];
  availableConsultants: ConsultantItem[];
  availableStores: StoreOption[];
  onOpenEmbed: (slug: string, name: string, formId?: string) => void;
}

export function EditorView({
  defaultValues, editingId, existingSlugs, onBack, onSaved,
  availableServices, availableConsultants, availableStores, onOpenEmbed,
}: EditorProps) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { toast } = useToast();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onBlur',
  });
  const { register, control, handleSubmit, watch, setValue, setError, clearErrors, formState: { errors, isSubmitting } } = form;
  const [slugChecking, setSlugChecking] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  // Bump dopo un salvataggio riuscito → forza il refetch del pannello coerenza.
  const [coherenceRefresh, setCoherenceRefresh] = useState(0);
  const watched = watch();
  const [bookingWindow, setBookingWindow] = useState({ minLeadTimeHours: 0, maxAdvanceDays: 90 });
  const [bookingWindowError, setBookingWindowError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    apiClient.get<unknown>('/calendar/settings').then((res) => {
      if (!res.success) throw new Error('Impossibile leggere gli anticipi del calendario');
      const settings = calendarSettingsRowSchema.pick({
        min_advance_booking: true, max_advance_booking: true,
      }).parse(res.data);
      if (alive) setBookingWindow({
        minLeadTimeHours: (settings.min_advance_booking ?? 0) / 60,
        maxAdvanceDays: settings.max_advance_booking ?? 90,
      });
    }).catch(() => {
      if (alive) setBookingWindowError('Impossibile leggere gli anticipi del calendario. Riprova dalle impostazioni generali.');
    });
    return () => { alive = false; };
  }, []);

  // Task #1211: il documento privacy del tenant è la single source of truth
  // (gestita nel CRM → Impostazioni → GDPR) anche per i form pubblici di
  // prenotazione. Lo mostriamo qui in sola lettura per orientare l'admin.
  // Task #2786 — content + consent_types servono per replicare in anteprima la
  // stessa view GDPR del form pubblico (box informativa + consensi dinamici).
  type TenantPrivacyConsentType = {
    key: string;
    label: string;
    required: boolean;
    description?: string;
  };
  type TenantPrivacyDoc = {
    id: string;
    version: number;
    title?: string;
    published_at?: string | null;
    content?: string;
    consent_types?: TenantPrivacyConsentType[];
  };
  const [tenantPrivacyDoc, setTenantPrivacyDoc] = useState<TenantPrivacyDoc | null>(null);
  const [tenantPrivacyDocLoading, setTenantPrivacyDocLoading] = useState(true);
  const [tenantPrivacyDocError, setTenantPrivacyDocError] = useState(false);
  useEffect(() => {
    let alive = true;
    setTenantPrivacyDocLoading(true);
    apiClient.get<unknown>('/crm/gdpr/privacy-document')
      .then((res) => {
        if (!alive) return;
        const { document: d } = crmPrivacyDocumentResponseSchema.parse(res);
        if (d && d.id) {
          // consent_types arriva come array JSON (o stringa JSON) dal backend.
          const rawConsents = d.consent_types;
          const parsedConsents: unknown = typeof rawConsents === 'string'
            ? (() => { try { return JSON.parse(rawConsents); } catch { return []; } })()
            : rawConsents;
          const consent_types: TenantPrivacyConsentType[] = Array.isArray(parsedConsents)
            ? parsedConsents
                .flatMap((x: unknown) => {
                  const parsed = z.object({ key: z.string() }).catchall(z.unknown()).safeParse(x);
                  return parsed.success ? [parsed.data] : [];
                })
                .map((x) => ({
                  key: String(x.key),
                  label: String(x.label ?? x.key),
                  required: !!x.required,
                  description: typeof x.description === 'string' ? x.description : undefined,
                }))
            : [];
          setTenantPrivacyDoc({
            id: d.id,
            version: Number(d.version) || 1,
            title: d.title,
            published_at: d.published_at,
            content: typeof d.content === 'string' ? d.content : '',
            consent_types,
          });
        } else {
          setTenantPrivacyDoc(null);
        }
      })
      .catch(() => {
        if (alive) {
          setTenantPrivacyDoc(null);
          setTenantPrivacyDocError(true);
        }
      })
      .finally(() => { if (alive) setTenantPrivacyDocLoading(false); });
    return () => { alive = false; };
  }, []);

  // Async slug uniqueness check, debounced. Uses the in-memory list of
  // existing forms (excluding the one being edited) — the backend has no
  // dedicated check-slug endpoint, but the DB unique constraint will reject
  // duplicates at save time as a safety net.
  const slugVal = watch('slug');
  useEffect(() => {
    if (!slugVal || slugVal.length < 3 || !SLUG_REGEX.test(slugVal)) return;
    setSlugChecking(true);
    const handle = setTimeout(() => {
      if (existingSlugs.includes(slugVal)) {
        setError('slug', { type: 'manual', message: 'Slug già in uso da un altro form' });
      } else {
        clearErrors('slug');
      }
      setSlugChecking(false);
    }, 350);
    return () => {
      clearTimeout(handle);
      setSlugChecking(false);
    };
  }, [slugVal, existingSlugs, setError, clearErrors]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = toApiPayload(values);
    try {
      if (editingId) {
        const res = await apiClient.put<unknown>(
          `/calendar/forms/${editingId}`,
          payload,
        );
        if (res.success && res.data) {
          const saved = appointmentFormDetailSchema.parse(res.data);
          setCoherenceRefresh((n) => n + 1);
          onSaved(saved.id, saved.slug, saved.name);
        }
      } else {
        const res = await apiClient.post<unknown>(
          '/calendar/forms',
          payload,
        );
        if (res.success && res.data) {
          const saved = appointmentFormDetailSchema.parse(res.data);
          setCoherenceRefresh((n) => n + 1);
          onSaved(saved.id, saved.slug, saved.name);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore salvataggio';
      toast({ title: msg, variant: 'destructive' });
    }
  });

  const customFields = useFieldArray({ control, name: 'customFields' });

  const toggleService = (id: string) => {
    const cur = watch('serviceIds');
    setValue('serviceIds', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id], { shouldDirty: true });
  };
  const toggleProduct = (id: string) => {
    const cur = watch('productIds') || [];
    setValue('productIds', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id], { shouldDirty: true });
  };
  const toggleConsultant = (id: string) => {
    const cur = watch('consultantIds');
    setValue('consultantIds', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id], { shouldDirty: true });
  };

  // Build live-preview config from current form values
  const previewConfig: AppointmentFormConfig = useMemo(() => ({
    // watched is deep-partial via react-hook-form; merge with defaults so the live
    // preview always has a complete config to render.
    ...DEFAULT_VALUES,
    id: watched.id || 'preview',
    slug: watched.slug || 'preview',
    name: watched.name,
    description: watched.description,
    isActive: watched.isActive,
    style: watched.style,
    branding: watched.branding,
    serviceIds: watched.serviceIds,
    consultantIds: watched.consultantIds,
    consultantFilterMode: watched.consultantFilterMode,
    // Task #2841 — il filtro competenze (modalità 'manual') deve arrivare in
    // anteprima dal valore corrente dell'editor, altrimenti restava al default
    // stale e la preview viva inviava un override skillFilter incoerente.
    consultantSkillFilter: watched.consultantSkillFilter,
    allowStoreChoice: watched.allowStoreChoice,
    // Task #2786 — anteprima in parità col form pubblico: sedi reali, prezzo e
    // modalità selezione servizio dai valori correnti dell'editor.
    stores: availableStores.map((s) => ({ id: s.id, name: s.name, city: s.city || undefined })),
    // Task #2786 — sede di default (read-only quando la scelta sede è OFF),
    // risolta dal `storeId` del form tra le sedi reali del tenant.
    defaultStore: (() => {
      const ds = availableStores.find((s) => s.id === watched.storeId);
      return ds ? { id: ds.id, name: ds.name, city: ds.city || undefined } : null;
    })(),
    serviceSelectionMode: watched.serviceSelectionMode,
    showPrice: watched.showPrice,
    rules: { ...watched.rules, ...bookingWindow },
    customFields: mapCustomFields(mergeCustomFields(watched)),
    privacy: { ...watched.privacy, pendingMessage: watched.privacy.successMessage },
    // Task #2786 — GDPR in anteprima identico al form pubblico: documento +
    // consensi dinamici dal documento privacy del tenant (no checkbox fallback).
    gdpr: {
      document: tenantPrivacyDoc
        ? {
            id: tenantPrivacyDoc.id,
            title: tenantPrivacyDoc.title || 'Informativa Privacy',
            content: tenantPrivacyDoc.content || '',
            version: tenantPrivacyDoc.version,
          }
        : null,
      consents: tenantPrivacyDoc?.consent_types ?? [],
    },
    notifications: watched.notifications,
    antiAbuse: {
      hcaptchaEnabled: watched.antiAbuse.captchaProvider === 'hcaptcha',
      hcaptchaSiteKey: watched.antiAbuse.captchaProvider === 'hcaptcha' ? watched.antiAbuse.captchaSiteKey : undefined,
      recaptchaEnabled: watched.antiAbuse.captchaProvider === 'recaptcha',
      recaptchaSiteKey: watched.antiAbuse.captchaProvider === 'recaptcha' ? watched.antiAbuse.captchaSiteKey : undefined,
    },
    captchaProvider: watched.antiAbuse.captchaProvider === 'none' ? undefined : watched.antiAbuse.captchaProvider,
  }), [watched, availableStores, tenantPrivacyDoc, bookingWindow]);

  // Anteprima: mostra SOLO i servizi/consulenti effettivamente selezionati nel form.
  // Se nessuno è selezionato, l'anteprima resta vuota (coerente col form pubblico reale,
  // che richiede whitelist esplicita per essere utilizzabile).
  const previewServices = useMemo(
    () => {
      const wlProducts = watched.productIds || [];
      const useProductWhitelist = wlProducts.length > 0;
      return availableServices
        .filter((s) => watched.serviceIds.includes(s.id))
        .map((s) => {
          // Filtra anche i prodotti (servizi card) sotto ogni tipologia in
          // base alla whitelist per-form. Se vuota, mostra tutti.
          const products = useProductWhitelist
            ? (s.products || []).filter((p) => wlProducts.includes(p.id))
            : (s.products || []);
          return { ...s, products };
        });
    },
    [availableServices, watched.serviceIds, watched.productIds],
  );
  // Task #2841 — pool consulenti per la preview STATICA (form non salvato, la
  // preview "viva" usa l'endpoint reale). Va reso coerente con la modalità: in
  // 'manual' (roster esplicito) si filtra per whitelist `consultantIds`; nelle
  // modalità skill/skill_shifts il backend filtra per skill, quindi la preview
  // statica mostra tutti i consulenti disponibili (approssimazione, niente
  // filtro fisso su `consultantIds`); in 'any' il widget non espone il pool.
  const previewConsultants = useMemo(
    () => watched.consultantFilterMode === 'manual'
      ? availableConsultants.filter((c) => watched.consultantIds.includes(c.id))
      : availableConsultants,
    [availableConsultants, watched.consultantIds, watched.consultantFilterMode],
  );

  const handlePhotoUpload = async (consultantId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('user_id', consultantId);
    try {
      const json = await apiClient.post<UserSitePhotoUpload>('/users/site-photo', fd);
      if (json.success && json.data?.site_photo_url) {
        // local optimistic update — parent reload will pick it up next
        const idx = availableConsultants.findIndex((c) => c.id === consultantId);
        if (idx >= 0) availableConsultants[idx]!.sitePhotoUrl = json.data.site_photo_url;
      } else {
        toast({ title: 'Foto non caricata', description: json.error || 'Upload fallito', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Foto non caricata', description: getErrorMessage(e), variant: 'destructive' });
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0 h-full">
      <div className="border-b bg-white dark:bg-gray-900 px-6 py-3 flex items-center gap-3 flex-wrap">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
        <div className="flex-1 min-w-[200px]">
          <Input
            {...register('name')}
            className="font-semibold border-none shadow-none px-2 text-lg"
            placeholder="Nome del form"
          />
          {errors.name && <p className="text-xs text-red-500 dark:text-red-400 mt-1 px-2">{errors.name.message}</p>}
        </div>
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch checked={field.value} onCheckedChange={field.onChange} />
              <span className="text-sm text-gray-600 dark:text-gray-300">{field.value ? 'Attivo' : 'Disattivo'}</span>
            </div>
          )}
        />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Salva
        </Button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden">
        {/* LEFT: configuration */}
        <div className="overflow-y-auto p-6 border-r min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full flex flex-wrap gap-1 h-auto p-1 justify-start">
              <TabsTrigger value="general" className="text-xs whitespace-nowrap"><Settings className="w-3 h-3 mr-1" />Generale</TabsTrigger>
              <TabsTrigger value="branding" className="text-xs whitespace-nowrap"><Palette className="w-3 h-3 mr-1" />Stile widget</TabsTrigger>
              <TabsTrigger value="services" className="text-xs whitespace-nowrap"><Briefcase className="w-3 h-3 mr-1" />Tipologie</TabsTrigger>
              <TabsTrigger value="consultants" className="text-xs whitespace-nowrap"><Users className="w-3 h-3 mr-1" />Consulenti</TabsTrigger>
              <TabsTrigger value="rules" className="text-xs whitespace-nowrap"><Sparkles className="w-3 h-3 mr-1" />Regole</TabsTrigger>
              <TabsTrigger value="fields" className="text-xs whitespace-nowrap"><ListChecks className="w-3 h-3 mr-1" />Campi</TabsTrigger>
              <TabsTrigger value="privacy" className="text-xs whitespace-nowrap"><Shield className="w-3 h-3 mr-1" />Privacy</TabsTrigger>
              <TabsTrigger value="notifications" className="text-xs whitespace-nowrap"><Bell className="w-3 h-3 mr-1" />Notifiche</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4 mt-4">
              <div>
                <Label>Slug pubblico *</Label>
                <div className="relative">
                  <Input
                    {...register('slug', {
                      setValueAs: (v: string) => (v || '').toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                    })}
                    placeholder="es. prenotazioni-roma"
                  />
                  {slugChecking && (
                    <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400 dark:text-gray-500" />
                  )}
                </div>
                {errors.slug ? (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.slug.message}</p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">URL pubblico: /{tenantSlug || 'tenant'}/booking/{watched.slug || 'slug'}</p>
                )}
              </div>
              <div>
                <Label>Sede *</Label>
                <Controller
                  name="storeId"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona la sede degli appuntamenti" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStores.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}{s.city ? ` — ${s.city}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.storeId ? (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.storeId.message}</p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sede di default: usata quando il cliente non sceglie una sede.</p>
                )}
                <Controller
                  name="allowStoreChoice"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-start gap-2 mt-3">
                      <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
                      <span>
                        <span className="text-sm font-medium">Lascia scegliere la sede al cliente</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          Nel form pubblico comparirà un selettore con tutte le sedi attive. Se disattivato, gli appuntamenti vengono assegnati alla sede di default qui sopra.
                        </span>
                      </span>
                    </div>
                  )}
                />
              </div>
              <div>
                <Label>Descrizione interna</Label>
                <Textarea {...register('description')} rows={3} placeholder="Note interne sulla finalità del form..." />
              </div>
            </TabsContent>

            <TabsContent value="branding" className="space-y-4 mt-4">
              <div>
                <Label>Stile widget</Label>
                <Controller
                  name="style"
                  control={control}
                  render={({ field }) => (
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {(['calcom', 'doctolib'] as const).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => field.onChange(s)}
                          className={`border rounded-lg p-3 text-left transition ${
                            field.value === s ? 'border-indigo-500 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          <div className="font-medium capitalize">{s === 'calcom' ? 'Cal.com' : 'Doctolib'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {s === 'calcom' ? 'Moderno, due colonne, calendario inline' : 'Wizard verticale, focus consulente'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorField label="Colore primario" name="branding.primaryColor" form={form} />
                <ColorField label="Colore accent" name="branding.accentColor" form={form} />
              </div>
              <div>
                <Label>Logo</Label>
                <Controller
                  name="branding.logoUrl"
                  control={control}
                  render={({ field }) => (
                    <LogoPicker value={field.value || ''} onChange={field.onChange} />
                  )}
                />
                {errors.branding?.logoUrl && <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.branding.logoUrl.message}</p>}
              </div>
              <div>
                <Label>Font</Label>
                <Controller
                  name="branding.fontFamily"
                  control={control}
                  render={({ field }) => {
                    const NONE = '__none__';
                    const CUSTOM = '__custom__';
                    const namedValues = FONT_OPTIONS.map((o) => o.value).filter((v) => v && v !== CUSTOM);
                    const isNamed = !!field.value && namedValues.includes(field.value);
                    const isEmpty = !field.value;
                    const selectValue = isEmpty ? NONE : isNamed ? field.value : CUSTOM;
                    return (
                      <div className="space-y-2">
                        <Select
                          value={selectValue}
                          onValueChange={(v) => {
                            if (v === NONE) field.onChange('');
                            else if (v === CUSTOM) field.onChange(field.value && !namedValues.includes(field.value) ? field.value : 'Inter, system-ui, sans-serif');
                            else field.onChange(v);
                          }}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FONT_OPTIONS.map((o) => (
                              <SelectItem key={o.label} value={o.value === '' ? NONE : o.value}>
                                <span style={{ fontFamily: o.value && o.value !== CUSTOM ? o.value : undefined }}>{o.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectValue === CUSTOM && (
                          <Input
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value)}
                            placeholder="es. 'Nunito Sans', system-ui, sans-serif"
                          />
                        )}
                      </div>
                    );
                  }}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Per font custom assicurati di caricarli nel sito che ospita il widget.</p>
              </div>
              <div>
                <Label>Tema predefinito</Label>
                <Controller
                  name="branding.defaultTheme"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">Chiaro</SelectItem>
                        <SelectItem value="dark">Scuro</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </TabsContent>

            <TabsContent value="services" className="space-y-3 mt-4">
              <div className="p-3 rounded-md bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-900 dark:text-indigo-100">
                <strong>Come funziona:</strong> qui scegli <em>quali tipologie</em> mostrare nel form pubblico.
                Sotto ogni tipologia trovi l'elenco dei <em>servizi</em> ad essa collegati: il cliente potrà sceglierne uno
                durante la prenotazione (in base alla modalità impostata nella tab "Consulenti").
                Per aggiungere o togliere servizi a una tipologia vai in <strong>Calendario → Impostazioni → Tipi Appuntamento → Servizi abbinati</strong>.
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Seleziona le tipologie di appuntamento che i clienti potranno prenotare. Per
                ogni tipologia puoi anche scegliere <strong>quali servizi</strong> mostrare
                cliccando sulle card sottostanti — se non ne selezioni nessuno, vengono
                mostrati tutti.
              </p>
              {availableServices.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nessun tipo appuntamento configurato. Vai in Calendario → Impostazioni per crearli.</p>
              ) : (
                <div className="space-y-2">
                  {availableServices.map((s) => {
                    const checked = watched.serviceIds.includes(s.id);
                    const products = s.products || [];
                    const wlProducts = watched.productIds || [];
                    const useProductWhitelist = wlProducts.length > 0;
                    // Conteggio dei servizi attivi su questa tipologia (per badge):
                    // se whitelist vuota → tutti; altrimenti → intersezione.
                    const activeProductsCount = useProductWhitelist
                      ? products.filter((p) => wlProducts.includes(p.id)).length
                      : products.length;
                    // IDs selezionati per ServiceCardList in modalità multi.
                    // Quando whitelist vuota, considero TUTTI selezionati visivamente
                    // (è il default: "tutti i servizi sono mostrati al cliente").
                    const selectedForCardList = useProductWhitelist
                      ? products.filter((p) => wlProducts.includes(p.id)).map((p) => p.id)
                      : products.map((p) => p.id);
                    return (
                      <div
                        key={s.id}
                        className={`border rounded-lg ${checked ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-900/10' : 'border-gray-200 dark:border-gray-700'}`}
                      >
                        <label className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer rounded-lg">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleService(s.id)}
                          />
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color }} />
                          <span className="font-medium text-sm flex-1 min-w-0 truncate">{s.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{s.durationMinutes} min</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${products.length > 0 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}
                            title={useProductWhitelist ? `${activeProductsCount} attivi su ${products.length} disponibili` : `${products.length} servizi (tutti attivi di default)`}
                          >
                            {useProductWhitelist
                              ? `${activeProductsCount}/${products.length}`
                              : `${products.length}`} {products.length === 1 ? 'servizio' : 'servizi'}
                          </span>
                        </label>
                        {checked && products.length > 0 && (
                          <div className="px-3 pb-3 pl-9">
                            {!useProductWhitelist && (
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1.5">
                                Tutti i servizi sono attivi. Clicca su una card per disattivarla solo per questo form.
                              </p>
                            )}
                            <ServiceCardList
                              mode="multi"
                              emptyText="Nessun servizio collegato a questa tipologia."
                              services={products.map((p) => ({
                                id: p.id,
                                name: p.name,
                                price: typeof p.price === 'number' ? p.price : null,
                                description: p.description ?? null,
                              }))}
                              selectedIds={selectedForCardList}
                              onToggle={(productId) => {
                                // Prima interazione partendo da whitelist vuota: il
                                // default è "tutti selezionati" → l'utente clicca per
                                // togliere uno → la whitelist diventa "tutti tranne lui".
                                // Nota: dobbiamo includere TUTTI i prodotti delle altre
                                // tipologie selezionate, altrimenti se la tipologia
                                // corrente ha un solo servizio la whitelist resterebbe
                                // vuota e il sistema la interpreterebbe come "tutti
                                // attivi" (bug: la deselezione non avrebbe effetto).
                                if (!useProductWhitelist) {
                                  const allFromOtherSelectedTypes = availableServices
                                    .filter((sv) => watched.serviceIds.includes(sv.id) && sv.id !== s.id)
                                    .flatMap((sv) => (sv.products || []).map((pp) => pp.id));
                                  const allButThisInCurrentType = products
                                    .filter((p) => p.id !== productId)
                                    .map((p) => p.id);
                                  setValue(
                                    'productIds',
                                    [...allFromOtherSelectedTypes, ...allButThisInCurrentType],
                                    { shouldDirty: true },
                                  );
                                } else {
                                  toggleProduct(productId);
                                  // Se dopo il toggle la whitelist contiene TUTTI i prodotti
                                  // di TUTTE le tipologie selezionate, la svuotiamo per
                                  // tornare al default "tutti" (più pulito da mostrare).
                                  // Lo facciamo nel prossimo tick valutando lo stato.
                                  setTimeout(() => {
                                    const cur = watch('productIds') || [];
                                    const allProductIds = (availableServices
                                      .filter((sv) => watched.serviceIds.includes(sv.id))
                                      .flatMap((sv) => (sv.products || []).map((pp) => pp.id)));
                                    if (allProductIds.length > 0 && allProductIds.every((id) => cur.includes(id))) {
                                      setValue('productIds', [], { shouldDirty: true });
                                    }
                                  }, 0);
                                }
                              }}
                            />
                            {useProductWhitelist && (
                              <button
                                type="button"
                                className="mt-2 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
                                onClick={() => {
                                  // Riattiva tutti i servizi di QUESTA tipologia.
                                  const otherTypeProducts = wlProducts.filter((id) => !products.some((p) => p.id === id));
                                  const allOfThisType = products.map((p) => p.id);
                                  setValue('productIds', [...otherTypeProducts, ...allOfThisType], { shouldDirty: true });
                                }}
                              >
                                Riattiva tutti i servizi di "{s.name}"
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <Controller
                name="showPrice"
                control={control}
                render={({ field }) => (
                  <div className="flex items-start justify-between gap-3 p-3 border rounded-lg bg-gray-50 dark:bg-gray-800 mt-3">
                    <div className="flex-1 min-w-0">
                      <Label className="text-sm">Mostra prezzo nel widget pubblico</Label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Se attivo, accanto al nome del servizio compare il prezzo (€). Disattiva per
                        servizi <strong>premium</strong>, B2B o "su preventivo" dove il prezzo si
                        discute caso per caso.
                      </p>
                    </div>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </div>
                )}
              />
            </TabsContent>

            <TabsContent value="consultants" className="space-y-3 mt-4">
              <div className="space-y-3 border rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                {/* Task #2786 — selettore unico modalità consulente (sostituisce i
                    due switch allowConsultantChoice + allowAnyConsultant). */}
                <Controller
                  name="consultantFilterMode"
                  control={control}
                  render={({ field }) => {
                    const MODES: Array<{ value: 'any' | 'skill_shifts' | 'manual'; label: string; help: string }> = [
                      { value: 'any', label: 'Assegnazione automatica', help: 'Il cliente non sceglie il consulente: il sistema lo assegna automaticamente tra quelli disponibili.' },
                      { value: 'skill_shifts', label: 'Competenze e turni', help: 'Il cliente sceglie tra i consulenti con la competenza richiesta dal servizio, mostrando solo gli slot coperti dai loro turni di lavoro.' },
                      { value: 'manual', label: 'Elenco manuale (whitelist)', help: 'Il cliente sceglie solo tra i consulenti che selezioni qui sotto: nessun filtro per turni.' },
                    ];
                    return (
                      <div className="space-y-1.5">
                        <Label className="text-sm">Modalità selezione consulente</Label>
                        <div className="space-y-2">
                          {MODES.map((m) => (
                            <label
                              key={m.value}
                              className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${field.value === m.value ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40' : 'border-gray-200 dark:border-gray-700'}`}
                            >
                              <input
                                type="radio"
                                className="mt-1"
                                name="consultantFilterMode"
                                value={m.value}
                                checked={field.value === m.value}
                                onChange={() => field.onChange(m.value)}
                              />
                              <span className="flex-1 min-w-0">
                                <span className="text-sm block">{m.label}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 block mt-0.5">{m.help}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  }}
                />
                {/* Task #515 + #525: come gestire la selezione "Servizio" (prodotto) nel widget pubblico.
                    Dal Task #525 il selettore è inline (dropdown sotto la card della tipologia). */}
                <Controller
                  name="serviceSelectionMode"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-1.5 pt-2 border-t">
                      <Label htmlFor="appointment-form-service-selection-mode" className="text-sm">Modalità selezione servizio</Label>
                      <Select
                        value={field.value}
                        onValueChange={(v) => field.onChange(appointmentFormBodySchema.shape.service_selection_mode.parse(v))}
                      >
                        <SelectTrigger id="appointment-form-service-selection-mode" className="w-full text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Automatica — visibile solo se ci sono più servizi collegati (opzionale)</SelectItem>
                          <SelectItem value="always">Sempre visibile — anche con un solo servizio collegato (opzionale)</SelectItem>
                          <SelectItem value="required">Obbligatorio — il cliente deve sceglierne uno</SelectItem>
                          <SelectItem value="never">Mai — non chiedere il servizio al cliente</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Controlla se nel form pubblico compare il dropdown "Servizio" sotto la tipologia
                        scelta. In modalità <strong>Obbligatorio</strong> il cliente non può proseguire
                        finché non lo seleziona.
                      </p>
                    </div>
                  )}
                />
              </div>

              {/* Controllo coerenza read-only tra la modalità del form e le regole
                  di disponibilità effettive del tenant (resolver). */}
              <FormCoherencePanel formId={editingId} refreshToken={coherenceRefresh} />

              {/* Task #2830 — il roster whitelist è la fonte UNICA dei consulenti
                  solo in modalità 'manual'; negli altri modi il pool è governato
                  da skill/turni e questo pannello non va mostrato. */}
              {watched.consultantFilterMode === 'manual' && (
                <>
              {/* Task #2846 — flag "filtro competenze": valido SOLO in modalità
                  manuale. Se attivo, la whitelist viene ulteriormente ristretta
                  ai consulenti con la competenza del servizio/prodotto scelto
                  (il backend lo applica solo con un prodotto selezionato). */}
              <Controller
                name="consultantSkillFilter"
                control={control}
                render={({ field }) => (
                  <div className="flex items-start justify-between gap-3 border rounded-lg p-3">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Filtra anche per competenze</Label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Mostra dell'elenco solo i consulenti che hanno la competenza richiesta
                        dal servizio/prodotto scelto. Si applica solo quando il cliente seleziona
                        un prodotto.
                      </p>
                    </div>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </div>
                )}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">Elenco consulenti selezionabili (whitelist)</p>
              {watched.consultantIds.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Whitelist vuota: nessun consulente sarà prenotabile. Seleziona almeno un consulente.
                </p>
              )}
              {availableConsultants.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nessun utente disponibile</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {availableConsultants.map((c) => (
                    <div key={c.id} className="border rounded-lg p-3 flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={watched.consultantIds.includes(c.id)}
                        onChange={() => toggleConsultant(c.id)}
                      />
                      {c.sitePhotoUrl ? (
                        <img src={c.sitePhotoUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-xs font-bold">
                          {c.firstName?.[0]}{c.lastName?.[0]}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.fullName}</div>
                        {c.bio && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.bio}</div>}
                      </div>
                      <label className="text-xs text-indigo-600 dark:text-indigo-400 cursor-pointer hover:underline">
                        <ImageIcon className="w-3.5 h-3.5 inline mr-1" />
                        Foto
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handlePhotoUpload(c.id, f);
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              )}
                </>
              )}
            </TabsContent>

            <TabsContent value="rules" className="space-y-3 mt-4">
              <Controller
                name="rules.autoConfirm"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="text-sm">Auto-conferma prenotazioni</Label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Se disattivo, le prenotazioni richiedono approvazione manuale</p>
                    </div>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </div>
                )}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Anticipo minimo e massimo seguono le impostazioni generali del calendario, non il singolo form.{' '}
                <Link to={`/${tenantSlug}/calendar/settings`} className="underline">Apri impostazioni calendario</Link>
              </p>
              {bookingWindowError ? <p role="alert" className="text-xs text-red-500">{bookingWindowError}</p> : (
                <p className="text-xs">Anticipo minimo: {bookingWindow.minLeadTimeHours * 60} minuti · massimo: {bookingWindow.maxAdvanceDays} giorni</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Annullamento cliente: preavviso (ore)</Label>
                  <Input type="number" min="0" {...register('rules.cancelWindowHours', { valueAsNumber: true })} />
                </div>
                <div>
                  <Label className="text-xs">Spostamento cliente: preavviso (ore)</Label>
                  <Input type="number" min="0" {...register('rules.rescheduleWindowHours', { valueAsNumber: true })} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="fields" className="space-y-3 mt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Nome, email e telefono sono sempre richiesti. Aggiungi qui campi extra.</p>
              {watched.customFieldsSource.some(value => !customFieldSchema.safeParse(value).success) && (
                <p role="status" className="text-sm text-muted-foreground">
                  Alcuni campi definiti via API non sono modificabili in questo editor.
                  Verranno conservati senza modifiche al salvataggio.
                </p>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  customFields.append({ id: `cf_${Date.now()}`, label: 'Nuovo campo', type: 'text', required: false })
                }
              >
                <Plus className="w-3 h-3 mr-1" /> Aggiungi campo
              </Button>
              <div className="space-y-2">
                {customFields.fields.map((f, idx) => (
                  <div key={f.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex gap-2">
                      <Input {...register(`customFields.${idx}.label`)} placeholder="Etichetta" className="flex-1" />
                      <Controller
                        name={`customFields.${idx}.type`}
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Testo</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="phone">Telefono</SelectItem>
                              <SelectItem value="textarea">Area testo</SelectItem>
                              <SelectItem value="select">Select</SelectItem>
                              <SelectItem value="checkbox">Checkbox</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => customFields.remove(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                      </Button>
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" {...register(`customFields.${idx}.required`)} />
                      Campo obbligatorio
                    </label>
                    {watched.customFields[idx]?.type === 'select' && (
                      <Controller
                        name={`customFields.${idx}.options`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            value={(field.value || []).join(', ')}
                            onChange={(e) =>
                              field.onChange(e.target.value.split(',').map((o) => o.trim()).filter(Boolean))
                            }
                            placeholder="Opzioni separate da virgola"
                          />
                        )}
                      />
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="privacy" className="space-y-3 mt-4">
              {/* Task #1211: blocco informativo — l'informativa GDPR è
                  centralizzata nel CRM e usata anche dai form pubblici. */}
              <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 mt-0.5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Informativa privacy gestita nel CRM
                    </p>
                    <p className="text-xs text-blue-800 dark:text-blue-200 mt-1">
                      Il documento privacy mostrato in questo form pubblico è lo stesso del CRM
                      (single source of truth). Per modificarne testo, versione o consensi richiesti
                      vai in CRM → Impostazioni → GDPR.
                    </p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {tenantPrivacyDocLoading ? (
                        <span className="text-xs text-blue-700 dark:text-blue-300">Caricamento…</span>
                      ) : tenantPrivacyDoc ? (
                        <>
                          <Badge variant="outline" className="text-xs">
                            {tenantPrivacyDoc.title || 'Informativa attiva'} · v{tenantPrivacyDoc.version}
                          </Badge>
                          {tenantPrivacyDoc.published_at && (
                            <span className="text-xs text-blue-700 dark:text-blue-300">
                              pubblicata il {new Date(tenantPrivacyDoc.published_at).toLocaleDateString('it-IT')}
                            </span>
                          )}
                        </>
                      ) : (
                        <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-300">
                          Nessuna informativa attiva
                        </Badge>
                      )}
                      {tenantSlug && (
                        <Link
                          to={`/${tenantSlug}/crm?settings=gdpr`}
                          className="text-xs font-medium text-blue-700 dark:text-blue-300 hover:underline inline-flex items-center gap-1"
                        >
                          Apri CRM → GDPR <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Label>Label checkbox accettazione (opzionale)</Label>
                <Textarea {...register('privacy.privacyText')} rows={2}
                  placeholder='Es. "Ho letto e accetto l’informativa privacy"' />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Etichetta personalizzata mostrata accanto alla spunta. Se vuota viene usato il default del widget.
                  Il testo dell'informativa completa è quello configurato nel CRM.
                </p>
              </div>
              <Controller
                name="privacy.marketingOptIn"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label>Mostra checkbox marketing opzionale</Label>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </div>
                )}
              />
              <div>
                <Label>Messaggio dopo la prenotazione (confermata o in attesa)</Label>
                <Textarea {...register('privacy.successMessage')} rows={2} />
              </div>
              <div>
                <Label>Reindirizzamento dopo la conferma (non attivo)</Label>
                <Input {...register('privacy.redirectUrl')} readOnly aria-disabled="true" />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Il widget mostra il riepilogo: non esegue reindirizzamenti. Il valore già salvato viene conservato.</p>
                {errors.privacy?.redirectUrl && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.privacy.redirectUrl.message}</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="notifications" className="space-y-3 mt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Invio email e allegati sono gestiti dalle automazioni del calendario. Queste preferenze storiche sono conservate, ma non configurano le automazioni.{' '}
                <Link to={`/${tenantSlug}/calendar/settings?section=automations`} className="underline">Apri automazioni</Link>
              </p>
              {(['emailCustomer', 'emailConsultant', 'attachIcs'] as const).map((key) => (
                <Controller
                  key={key}
                  name={`notifications.${key}`}
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>
                        {key === 'emailCustomer' && 'Email di conferma al cliente'}
                        {key === 'emailConsultant' && 'Email al consulente assegnato'}
                        {key === 'attachIcs' && 'Allega invito calendario (.ics)'}
                      </Label>
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled />
                    </div>
                  )}
                />
              ))}

              <div className="border-t pt-3">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Anti-abuso
                </Label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
                  Un campo "honeypot" invisibile è sempre attivo per scoraggiare i bot più semplici.
                  Per protezione robusta puoi aggiungere una sfida CAPTCHA (hCaptcha o Google reCAPTCHA).
                </p>
                <Controller
                  name="antiAbuse.captchaProvider"
                  control={control}
                  render={({ field }) => (
                    <div className="p-3 border rounded-lg">
                      <Label>Provider CAPTCHA</Label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">
                        Mostra una sfida CAPTCHA prima di confermare la prenotazione. Riduce drasticamente lo spam.
                      </p>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuno (solo honeypot)</SelectItem>
                          <SelectItem value="hcaptcha">hCaptcha</SelectItem>
                          <SelectItem value="recaptcha">Google reCAPTCHA v2</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                />
                {watched.antiAbuse.captchaProvider !== 'none' && (
                  <div className="mt-2 space-y-2 p-3 border rounded-lg bg-amber-50/40 dark:bg-amber-900/20">
                    <div>
                      <Label className="text-xs">
                        {watched.antiAbuse.captchaProvider === 'recaptcha' ? 'reCAPTCHA Site Key' : 'hCaptcha Site Key'}
                      </Label>
                      <Input
                        {...register('antiAbuse.captchaSiteKey')}
                        placeholder={
                          watched.antiAbuse.captchaProvider === 'recaptcha'
                            ? 'es. 6LcXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'
                            : 'es. 10000000-ffff-ffff-ffff-000000000001'
                        }
                      />
                      {errors.antiAbuse?.captchaSiteKey && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.antiAbuse.captchaSiteKey.message}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">
                        {watched.antiAbuse.captchaProvider === 'recaptcha' ? 'reCAPTCHA Secret Key' : 'hCaptcha Secret Key'}
                      </Label>
                      <Input
                        type="password"
                        autoComplete="off"
                        {...register('antiAbuse.captchaSecretKey')}
                        placeholder={
                          watched.antiAbuse.captchaSecretConfigured
                            && watched.antiAbuse.captchaSecretConfiguredFor === watched.antiAbuse.captchaProvider
                            ? '•••••••••• (già configurata, lascia vuoto per non modificare)'
                            : watched.antiAbuse.captchaProvider === 'recaptcha'
                              ? 'es. 6LcXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'
                              : 'es. 0xXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'
                        }
                      />
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Salvata cifrata sul server e mai restituita al client. Usata per la verifica server-to-server del token.
                      </p>
                      {errors.antiAbuse?.captchaSecretKey && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.antiAbuse.captchaSecretKey.message}</p>
                      )}
                    </div>
                    {watched.antiAbuse.captchaProvider === 'hcaptcha' ? (
                      <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed">
                        Crea un account gratuito su{' '}
                        <a
                          href="https://www.hcaptcha.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-0.5"
                        >
                          hcaptcha.com <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                        , aggiungi il dominio del sito che ospita il widget e copia qui la <strong>Site Key</strong> pubblica e la <strong>Secret Key</strong> privata.
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed">
                        Registra il sito su{' '}
                        <a
                          href="https://www.google.com/recaptcha/admin"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-0.5"
                        >
                          google.com/recaptcha/admin <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                        , scegli <strong>reCAPTCHA v2 — "Non sono un robot"</strong>, aggiungi il dominio del sito che ospita il widget e copia qui la <strong>Site Key</strong> pubblica e la <strong>Secret Key</strong> privata.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>


        </div>

        {/* RIGHT: live preview — responsive: padding ridotto su schermi stretti
            e wrapper con min-w-0 + overflow-x-auto per evitare l'orizzontale
            sui layout con sidebar/topbar che riducono lo spazio disponibile. */}
        <div className="overflow-y-auto bg-gray-100 dark:bg-gray-800 p-2 sm:p-3 lg:p-5 min-h-0 min-w-0">
          <div className="flex items-center gap-2 mb-2 sm:mb-3 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium truncate">Anteprima live</span>
          </div>
          {/* Wrapper dell'anteprima: il widget pubblico è renderizzato a
              larghezza nativa (PREVIEW_NATIVE_WIDTH px) e poi rimpicciolito
              tramite CSS transform: scale() in modo da entrare nello spazio
              disponibile, come fosse uno zoom-out. Niente scroll. */}
          <ScaledPreview>
            {tenantPrivacyDocError && (
              <p role="alert" className="mb-3 text-sm text-destructive">
                Impossibile caricare il documento privacy e i consensi del tenant.
                L’anteprima non rappresenta la configurazione GDPR effettiva.
              </p>
            )}
            <AppointmentWidget
              slug={previewConfig.slug}
              preview
              previewLive={!!editingId}
              styleOverride={previewConfig.style}
              initialConfig={previewConfig}
              initialServices={previewServices}
              initialConsultants={previewConsultants}
              // Task #2846 — l'anteprima live passa il JWT admin così le chiamate
              // di lettura sono esonerate dal rate limit pubblico (no 429).
              apiOptions={{ authToken: localStorage.getItem('edgvoip_tenant_token') || undefined }}
            />
          </ScaledPreview>
        </div>
      </div>
    </form>
  );
}

interface ColorFieldProps {
  label: string;
  name: 'branding.primaryColor' | 'branding.accentColor';
  form: UseFormReturn<FormValues>;
}
function ColorField({ label, name, form }: ColorFieldProps) {
  const { register, watch, formState: { errors } } = form;
  const v = watch(name);
  const err = name === 'branding.primaryColor'
    ? errors.branding?.primaryColor?.message
    : errors.branding?.accentColor?.message;
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 items-center">
        <input type="color" value={v} onChange={(e) => form.setValue(name, e.target.value, { shouldDirty: true })} className="w-14 h-10 p-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800" />
        <Input {...register(name)} />
      </div>
      {err && <p className="text-xs text-red-500 dark:text-red-400 mt-1">{err}</p>}
    </div>
  );
}

interface LogoPickerProps {
  value: string;
  onChange: (v: string) => void;
}
function LogoPicker({ value, onChange }: LogoPickerProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState<'upload' | 'url'>(value && /^https?:\/\//i.test(value) && !value.startsWith('/api/landing-pages/images/') ? 'url' : 'upload');

  const handleFile = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File troppo grande', description: 'Massimo 5MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const json = await apiClient.post<AppointmentFormLogoUpload>('/calendar/forms/upload-logo', fd);
      if (!json.success || !json.data) {
        throw new Error(json.error || 'Upload fallito');
      }
      onChange(json.data.url);
      toast({ title: 'Logo caricato' });
    } catch (e) {
      toast({ title: 'Errore upload', description: getErrorMessage(e), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1 text-xs">
        <button
          type="button"
          className={`px-2 py-1 rounded ${mode === 'upload' ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          onClick={() => setMode('upload')}
        >
          Carica file
        </button>
        <button
          type="button"
          className={`px-2 py-1 rounded ${mode === 'url' ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          onClick={() => setMode('url')}
        >
          URL esterno
        </button>
      </div>

      {mode === 'upload' ? (
        <div className="flex items-center gap-3">
          {value ? (
            <div className="relative w-16 h-16 border rounded-lg overflow-hidden bg-white dark:bg-gray-900 flex items-center justify-center shrink-0">
              <img src={value} alt="logo" className="max-w-full max-h-full object-contain" />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute top-0 right-0 bg-white/90 dark:bg-gray-900/90 rounded-bl text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 p-0.5"
                title="Rimuovi logo"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="w-16 h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 shrink-0">
              <ImageIcon className="w-6 h-6" />
            </div>
          )}
          <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer transition ${uploading ? 'opacity-50 pointer-events-none' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Caricamento…' : value ? 'Sostituisci' : 'Scegli immagine'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {value ? (
            <div className="w-16 h-16 border rounded-lg overflow-hidden bg-white dark:bg-gray-900 flex items-center justify-center shrink-0">
              <img src={value} alt="logo" className="max-w-full max-h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          ) : (
            <div className="w-16 h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 shrink-0">
              <ImageIcon className="w-6 h-6" />
            </div>
          )}
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…/logo.png"
            className="flex-1"
          />
        </div>
      )}
      <p className="text-[11px] text-gray-500 dark:text-gray-400">PNG, JPG, WEBP, SVG · max 5MB</p>
    </div>
  );
}
